import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/Feedback'
import { FilterBar } from '@/components/catalog/FilterBar'
import { WatchGrid } from '@/components/catalog/WatchGrid'
import { useWatches } from '@/hooks/useWatches'
import { EMPTY_FILTERS, filterWatches, maisonsOf, publicWatches } from '@/lib/catalog'
import type { CatalogFilters } from '@/types'

/**
 * The catalogue.
 *
 * The collection filter is mirrored in the URL (`?collection=dive`) so the
 * collection tiles on the home page can link straight into a filtered view and
 * the result stays shareable.
 */
export default function Collection() {
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()
  const { watches, collections, loading } = useWatches()

  const [filters, setFilters] = useState<CatalogFilters>(() => ({
    ...EMPTY_FILTERS,
    collection: params.get('collection') ?? '',
    q: params.get('q') ?? '',
  }))

  // Keep the URL honest about what is on screen, without stacking history
  // entries — every keystroke in the search box would otherwise be a "back".
  useEffect(() => {
    const next = new URLSearchParams(params)
    filters.collection ? next.set('collection', filters.collection) : next.delete('collection')
    filters.q ? next.set('q', filters.q) : next.delete('q')
    if (next.toString() !== params.toString()) setParams(next, { replace: true })
  }, [filters.collection, filters.q, params, setParams])

  const live = useMemo(() => publicWatches(watches), [watches])
  const results = useMemo(() => filterWatches(live, filters, collections), [live, filters, collections])
  const maisons = useMemo(() => maisonsOf(live), [live])
  const query = filters.q.trim()

  return (
    <div data-upbo-id="page-collection" data-upbo-label="Collection page">
      {/* ── Page head ──────────────────────────────────────────────────── */}
      <Container size="xl">
        <div className="pt-14 sm:pt-20 pb-10 flex flex-col gap-4 max-w-2xl">
          <span className="eyebrow">{t('catalog.eyebrow')}</span>
          {/* Arriving from a search? Say so in the heading — a page that looks
              identical whether or not it is filtered is how people get lost. */}
          <h1 className="font-display text-[42px] sm:text-[56px] leading-[1.03]">
            {query ? t('search.resultsFor', { q: query }) : t('catalog.title')}
          </h1>
          <p className="text-[15px] text-text-muted">{t('catalog.subtitle')}</p>
        </div>
      </Container>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="border-y border-border bg-surface">
        <Container size="xl" className="py-6">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(EMPTY_FILTERS)}
            collections={collections}
            maisons={maisons}
            resultCount={results.length}
          />
        </Container>
      </div>

      {/* ── Results ────────────────────────────────────────────────────── */}
      <Container size="xl" className="py-12 sm:py-16">
        {!loading && results.length === 0 ? (
          <EmptyState
            icon={<Search size={22} strokeWidth={1.5} />}
            title={query ? t('search.noResults', { q: query }) : t('catalog.emptyTitle')}
            description={query ? t('search.noResultsBody') : t('catalog.emptyBody')}
            action={
              <Button variant="outline" onClick={() => setFilters(EMPTY_FILTERS)}>
                {t('catalog.clearFilters')}
              </Button>
            }
          />
        ) : (
          <WatchGrid watches={results} loading={loading} columns={3} query={query} />
        )}
      </Container>
    </div>
  )
}
